import { CutlerySolidIcon, ProgressActivityOutlineIcon } from '@ikyucom/design-system-icons-react'
import { json } from '@remix-run/node'
import type {
  LinkDescriptor,
  LinksFunction,
  LoaderFunctionArgs,
  MetaFunction,
} from '@remix-run/node'
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useRouteError,
  useRouteLoaderData,
} from '@remix-run/react'
import { captureRemixErrorBoundaryError } from '@sentry/remix'
import { useEffect } from 'react'
import { SWRConfig } from 'swr'
import ErrorBody from '~/components/error/ErrorBody'
import { SdFooter } from '~/components/footer/sd'
import ErrorHeader from '~/components/header/sd/ErrorHeader'
import { InitialImageReplacer } from '~/components/root/InitialImageReplacer'
import { Providers } from '~/components/root/providers'
import type { EnvType } from '~/env'
import { env } from '~/env'
import { nocache } from '~/headers'
import { isBlank } from '~/lib/blank/blank'
import { useLoading } from '~/lib/loading'
import { debug } from '~/lib/log'
import { alwaysFalse } from '~/lib/remix'
import { baseMeta } from '~/lib/seo/meta'
import { type Device, xUaDevice } from '~/lib/ua'
import { useInitSentry } from '~/sentry'
import { css, cx, sva } from '~/styled-system/css'
import style from '~/styles/globals.css?url'
import mapboxStyle from '~/styles/mapbox.css?url'
import panda from '~/styles/panda.css?url'
import { Button } from '~/ui'
import { parseCookie } from './lib/cookie'
import { isEmpty } from './lib/empty'
import { handleMiddlewareError } from './lib/error/serverError'
import { vstack } from './styled-system/patterns'

export const meta: MetaFunction = baseMeta

// 新 PPG でアイコンが変わったことを Googlebot に認識させるために
// `/favicon.ico` とは異なる path で favicon, touch icon を提供する
// (一休も古いままなので PPG で効果があれば追随したい)
// なお、ブラウザは HTML を読む前に favicon.ico を取得しようとするので
// favicon.ico にリクエストが来たときも `/ppg_favicon.ico` を返す措置を Fastly で行っている
const [favicon, appleTouchIcon] =
  import.meta.env.VITE_MODE === 'ppg'
    ? (['/ppg_favicon.ico', 'ppg_app_icon_144.png'] as const)
    : (['/favicon.ico', '/app_icon_144.png'] as const)

const mapBoxLink: LinkDescriptor[] =
  import.meta.env.VITE_MODE === 'ppg' ? [{ rel: 'stylesheet', href: mapboxStyle }] : []

export const links: LinksFunction = () => {
  return [
    ...mapBoxLink,
    { rel: 'stylesheet', href: style },
    { rel: 'stylesheet', href: panda },
    { rel: 'icon', href: favicon },
    { rel: 'apple-touch-icon-precomposed', href: appleTouchIcon },
  ]
}

// リリース時に env が必ず新しい値が返ってくることを保証したい
// ただ shouldRevalidate が常に false なので loader が呼ばれるタイミングは存在しないのでは？
export async function loader({ request, context }: LoaderFunctionArgs) {
  handleMiddlewareError(context)
  const cookie = parseCookie(request.headers.get('Cookie') || '')
  context.cookie = cookie
  const device = xUaDevice(request.headers)
  const FEATURE_NEW_PRICE_RANGE =
    env.FEATURE_NEW_PRICE_RANGE === '1' || request.headers.get('ir-macaron-guide') === '1'
      ? '1'
      : '0'
  return json({ env: { ...env, FEATURE_NEW_PRICE_RANGE }, device, cookie }, { headers: nocache() })
}

// env は常に同じなので再取得は不要
export const shouldRevalidate = alwaysFalse

// TODO: 以下と同じタイミングで環境変数 MY_IKYU_DOMAIN の命名を整理する
// https://github.com/ikyucom/macaron-frontend/issues/4904
const ikyuLoginScriptUrls =
  import.meta.env.VITE_MODE === 'ppg'
    ? // PPG の場合は MY_IKYU_DOMAIN に paypaygourmet.yahoo.co.jp ドメインが設定されている
      // 分かりづらいので、そもそも上記のTODOで環境変数名自体を変えたい
      [`https://${env.MY_IKYU_DOMAIN}/assets/ppg-authenticator.min.js`]
    : [
        `https://${env.MY_IKYU_DOMAIN}/assets/ikyu-login.min.js`,
        `https://${env.MY_IKYU_DOMAIN}/assets/ikyu-authenticator.min.js`,
      ]

export default function App() {
  const { env, device, cookie } = useLoaderData<typeof loader>()
  // FIXME use hydrate atom!
  useInitSentry(env as unknown as EnvType)
  return (
    <html lang="ja" prefix="og: https://ogp.me/ns#">
      <head>
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1.0, viewport-fit=cover"
        />
        <Meta />
        <Links />
        <script src={env.CARD_PAYMENT_TOKEN_SERVICE_URL} async />
        <script src={env.PAYJP_SERVICE_URL} async />
        {ikyuLoginScriptUrls.map((url) => (
          <script key={url} src={url} async />
        ))}
        <script src={`https://${env.MY_IKYU_DOMAIN}/member/state/refresh`} async />
        {!isEmpty(env.IKYU_ANALYTICS_POPUP_SCRIPT_URL) && (
          <script src={env.IKYU_ANALYTICS_POPUP_SCRIPT_URL} async />
        )}
        <script src={env.IKYU_ANALYTICS_URL} async />
        {isBlank(env.GOOGLE_TAG_MANAGER_ID) || (
          <script
            dangerouslySetInnerHTML={{
              __html: `
(function (w, d, s, l, i) {
  w[l] = w[l] || [];
  w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
  var f = d.getElementsByTagName(s)[0], j = d.createElement(s), dl = l != 'dataLayer' ? '&l=' + l : '';
  j.async = true;
  j.src = '//www.googletagmanager.com/gtm.js?id=' + i + dl;
  f.parentNode.insertBefore(j, f);
})(window, document, 'script', 'dataLayer', '${env.GOOGLE_TAG_MANAGER_ID}');`,
            }}
          />
        )}
        <ScriptTags device={device} env={env as unknown as EnvType} />
      </head>
      <body
        className={cx(
          // max-width はモーダルダイアログが開いたときの幅調整のために設定
          // https://github.com/ikyucom/macaron-frontend/pull/4166#discussion_r1780361087
          'max-w-[100dvw] bg-neutral-000 font-sans text-base text-neutral-800',
          // Panda CSS の custom condition _pc, _sd は body の子孫要素でしか使えないので device で直接判定
          // PC の min-width は content.base の 1000px に px: 3 で左右 12px ずつ足した値を設定
          device !== 'sd' && css({ minWidth: '[1024px]' })
        )}
        // tablet は pc とみなすため device から一度変換
        data-device={device === 'sd' ? 'sd' : 'pc'}
      >
        {isBlank(env.GOOGLE_TAG_MANAGER_ID) || (
          <noscript>
            <iframe
              src={`//www.googletagmanager.com/ns.html?id=${env.GOOGLE_TAG_MANAGER_ID}`}
              height="0"
              width="0"
              style={{ display: 'none', visibility: 'hidden' }}
            />
          </noscript>
        )}
        <InitialImageReplacer />
        <SWRConfig
          value={{ errorRetryCount: 5, shouldRetryOnError: true, errorRetryInterval: 1000 }}
        >
          {/* loader から得られる Jsonify は互換性がない... */}
          <Providers env={env as unknown as EnvType} device={device} cookie={cookie}>
            <Outlet />
            <Loading />
          </Providers>
        </SWRConfig>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  )
}

const ScriptTags =
  import.meta.env.VITE_MODE === 'ppg'
    ? import.meta.env.VITE_APP_ENV === 'production' || import.meta.env.VITE_APP_ENV === 'staging'
      ? PpgScriptTags
      : DummyPpgScriptTags
    : Empty

function PpgScriptTags({ device, env }: { device: Device; env: EnvType }) {
  return (
    <>
      <PpgUserActionLogger device={device} />
      <PpgEMG device={device} env={env} />
    </>
  )
}

function PpgUserActionLogger({ device }: { device: Device }) {
  const opttype = device === 'pc' ? 'pc' : device === 'sd' ? 'smartphone' : 'tablet'
  return (
    <>
      <script
        dangerouslySetInnerHTML={{
          __html: `
            var TLDataContext = {
              ual: {
                pageview: {
                  opttype: "${opttype}",
                  id_type: "paypaygourmet",
                  nopv: "false"
                }
              }
            }
          `,
        }}
      />
      <script
        async
        src="https://s.yimg.jp/images/ds/managed/1/managed-ual.min.js?tk=da341495-b6bf-478a-97d8-8bd91d500daf&service=paypaygourmet"
      />
    </>
  )
}

type EMGProps = { device: Device; env?: EnvType }

function useInitEMG({ device }: EMGProps) {
  useEffect(() => {
    let timeout: NodeJS.Timeout
    function init() {
      const div = document.getElementById('TEMPLA_EMG_CONTAINER')
      if (window?.YAHOO?.JP?.templa?.EMG?.load === undefined || div === null) {
        timeout = setTimeout(init, 100)
        return
      }
      // ランディング時に load を読んでいるのでフラグを設定し、
      // navigation/yahoo/emg/index.ts の callEmg 内で
      // soft navigation 後の新しいヘッダーを待つことができるようにする
      div.dataset.emgLoadCalled = 'true'
      window.YAHOO.JP.templa.EMG.load({
        device_id: device === 'sd' ? 'smartphone' : 'pc',
      })
    }
    init()
    return () => clearTimeout(timeout)
  }, [device])
}

function DummyPpgScriptTags({ device, env }: EMGProps) {
  useInitEMG({ device })
  if (env?.ENABLE_TEST_EMG !== '1') {
    return
  }
  return (
    <script
      dangerouslySetInnerHTML={{
        __html: `
window.YAHOO = {
  JP: {
    templa: {
      EMG: {
        load: function ({ device_id }) {
          const fetch = () => {
            return new Promise((resolve) => {
              // 本番を想定してランダムな待ち時間（500〜1500ms）を設定
              const delay = Math.floor(Math.random() * 1000) + 500
              setTimeout(() => {
                resolve({
                  id: 'emg-banner',
                  hasData: true,
                  message: '一休確認用EMGバナー',
                })
              }, delay)
            })
          }

          fetch().then((data) => {
            if (data.hasData) {
              const emgElement = document.getElementById('TEMPLA_EMG')
              if (emgElement) {
                // 本番同様に重複チェックを入れる
                if (document.getElementById(data.id)) {
                  return
                }
                const testBanner = document.createElement('div')
                testBanner.setAttribute('id', data.id)
                testBanner.style.backgroundColor = '#ffcc00'
                testBanner.style.padding = '10px'
                testBanner.style.textAlign = 'center'
                testBanner.style.fontWeight = 'bold'
                testBanner.style.borderRadius = '4px'
                testBanner.style.margin = '10px 0'
                testBanner.innerHTML = data.message
                emgElement.appendChild(testBanner)
              }
            }
          })
        },
      },
    },
  },
}
            `,
      }}
    />
  )
}

function PpgEMG({ device }: EMGProps) {
  useInitEMG({ device })
  return <script defer src="https://s.yimg.jp/images/templa/mhdemg/v2/templa.min.js"></script>
}

function Empty() {
  return null
}

const loadingStyles = sva({
  slots: ['container', 'spinner', 'icon'],
  base: {
    container: {
      position: 'fixed',
      top: '[calc(50dvh - 24px)]',
      zIndex: '[10]',
      left: '[calc(50dvw - 24px)]',
    },
    spinner: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      rounded: 'full',
      padding: '3',
      bgColor: 'white',
      animation: 'loading',
      shadow: 'outline.lg',
    },
    icon: {
      h: '[24px]',
      w: '[24px]',
      color: 'tertiary',
    },
  },
})

function Loading() {
  const styles = loadingStyles()
  const loading = useLoading()

  useEffect(() => {
    debug(`[loading] state changed: ${loading}`)
  }, [loading])

  if (!loading) {
    return null
  }
  return (
    <div className={styles.container}>
      <div className={styles.spinner}>
        <ProgressActivityOutlineIcon className={styles.icon} />
      </div>
    </div>
  )
}

export function ErrorBoundary() {
  const data = useRouteLoaderData<typeof loader>('root')
  const error = useRouteError()
  captureRemixErrorBoundaryError(error)
  const errorBody = isNotFound(error) ? <NotFound /> : <OtherError error={error} />
  return (
    <html lang="ja">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1.0" />
        <Meta />
        <Links />
      </head>
      <body
        className="bg-neutral-000 font-sans text-base text-neutral-800"
        // 存在しないパスの場合はそもそも root loader を通らないので data が存在しないことがある
        // tablet は pc とみなすため device から一度変換
        data-device={data?.device === 'sd' ? 'sd' : 'pc'}
      >
        {errorBody}
        <Scripts />
      </body>
    </html>
  )
}

// loader から送られてくるのは Response と同じ interface だが serialize された別の型
// そこで number の status があるかどうかで判断している
function isNotFound(error: any) {
  return (
    error instanceof Object &&
    'status' in error &&
    typeof error.status === 'number' &&
    error.status === 404
  )
}

function OtherError({ error }: { error: any }) {
  return (
    <>
      <ErrorHeader />
      <div
        className={vstack({
          justifyContent: 'space-between',
          h: 'screen.height.full',
          mt: '5',
          mx: '3',
        })}
      >
        <ErrorBody error={error} />
        <SdFooter type="minimum" />
      </div>
    </>
  )
}

function NotFound() {
  return (
    <>
      <ErrorHeader />
      <NotFoundBody />
    </>
  )
}

const siteName = import.meta.env.VITE_MODE === 'ppg' ? 'PayPay グルメ' : '一休.comレストラン'
function NotFoundBody() {
  return (
    <>
      <div className={vstack({ px: '4', py: '12' })}>
        <div className={vstack({ gap: '3', mb: '5', textAlign: 'center' })}>
          <CutlerySolidIcon
            width={76}
            height={76}
            className={css({ display: 'inline', color: 'gray.500' })}
          />
          <p>ご指定のページは現在利用できないか、削除、変更された可能性があります。</p>
        </div>
        {/* FIXME panda.config.ts で button.tertiary.background を修正は別で行い bgColor: white の指定を不要にする */}
        <Button
          color="tertiary"
          css={{ textAlign: 'center', bgColor: 'white', _sd: { w: 'full' } }}
          asChild
        >
          <a href="/">{siteName}トップ</a>
        </Button>
      </div>
      <SdFooter type="minimum" />
    </>
  )
}
